// Create / edit a team: name, tag, coach TeamSpeak name. Only these fields are sent, so an edit
// never touches the team's logo URL. Enter submits; the button is disabled while saving.
import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import AnimatedModal from '../AnimatedModal';

export default function TeamFormModal({ team, isOpen, onClose, onSaved, onError }) {
  const { t } = useTranslation();
  const editing = !!(team && team.id);
  const [form, setForm] = useState({ name: '', tag: '', coach_name: '' });
  const [saving, setSaving] = useState(false);
  // state updates too late to stop a second click in the same tick — the ref doesn't
  const savingRef = useRef(false);

  useEffect(() => {
    if (isOpen) setForm({ name: (team && team.name) || '', tag: (team && team.tag) || '', coach_name: (team && team.coach_name) || '' });
  }, [isOpen, team]);

  const submit = async (e) => {
    e.preventDefault();
    if (savingRef.current || !form.name.trim()) return;
    savingRef.current = true;
    setSaving(true);
    try {
      const body = { name: form.name.trim(), tag: form.tag.trim(), coach_name: form.coach_name.trim() };
      const res = editing ? await axios.put(`/api/teams/${team.id}`, body) : await axios.post('/api/teams', body);
      onSaved(editing ? res.data.team : res.data, editing);
    } catch (err) {
      onError(err.response?.data?.error || t('teams.saveFailed', 'Failed to save team'));
    } finally {
      savingRef.current = false;
      setSaving(false);
    }
  };

  return (
    <AnimatedModal
      isOpen={isOpen}
      onClose={() => { if (!saving) onClose(); }}
      title={editing ? t('teams.editTeam', 'Edit team') : t('teams.createTeam', 'Create Team')}
      maxWidth="460px"
    >
      <form onSubmit={submit} className="tm-dlg">
        <div>
          <label className="tm-label" htmlFor="tm-f-name">{t('teams.teamName', 'Team name')} *</label>
          <input id="tm-f-name" className="tm-input" autoFocus required maxLength={64} value={form.name}
            onChange={e => setForm({ ...form, name: e.target.value })} />
        </div>
        <div className="tm-form-pair">
          <div className="narrow">
            <label className="tm-label" htmlFor="tm-f-tag">{t('teams.tag', 'Tag')}</label>
            <input id="tm-f-tag" className="tm-input mono" maxLength={6} placeholder={t('teams.tagPlaceholder', 'e.g. NAVI')} value={form.tag}
              onChange={e => setForm({ ...form, tag: e.target.value })} />
          </div>
          <div className="wide">
            <label className="tm-label" htmlFor="tm-f-coach">{t('teams.coachTs', 'Coach TeamSpeak name')}</label>
            <input id="tm-f-coach" className="tm-input" maxLength={64} placeholder={t('teams.coachTsPlaceholder', 'Must match the TS nickname exactly')} value={form.coach_name}
              onChange={e => setForm({ ...form, coach_name: e.target.value })} />
          </div>
        </div>
        <p className="tm-hint flush">{t('teams.formHint', 'Players, the logo and the in-game avatar are managed in the team\'s roster panel.')}</p>
        <div className="tm-dlg-foot">
          <button type="button" className="tm-btn" onClick={onClose} disabled={saving}>{t('common.cancel', 'Cancel')}</button>
          <button type="submit" className="tm-btn primary" disabled={saving || !form.name.trim()}>
            {saving && <span className="tm-spin" />}
            {editing ? t('common.saveChanges', 'Save changes') : t('teams.createTeam', 'Create Team')}
          </button>
        </div>
      </form>
    </AnimatedModal>
  );
}
